import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import { useAdditionalFields } from "../hooks/addition-fields";
import { useLocale } from "../stores/locale";
import { AdditionalFields } from "./additional-fields";
import { Button } from "./button";
import { Input } from "./input";

export type OrganizationFormValues = {
  corporateRegistrationNumber: string;
  name: string;
  email: string;
  phone: string;
  additionalFields?: Record<string, string>;
};

export function OrganizationForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
}: {
  onCancel: () => void;
  onChange: (values: OrganizationFormValues) => void;
  onSubmit: () => void;
  values: OrganizationFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const t = useLocale((state) => state.locale);
  const { data: additionalFields } = useAdditionalFields("organization");

  const isValid = useMemo(() => {
    return (
      values.corporateRegistrationNumber !== "" &&
      additionalFields.every(
        (field) =>
          !field.required ||
          values.additionalFields?.[field.name]?.trim() !== "",
      )
    );
  }, [values, additionalFields]);
  const isDisabled = useMemo(() => {
    return disabled || loading;
  }, [disabled, loading]);

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Input
          required
          disabled={isDisabled}
          label={t.organization.corporateRegistrationNumber}
          type="text"
          value={values.corporateRegistrationNumber}
          onChange={(e) =>
            onChange({ ...values, corporateRegistrationNumber: e.target.value })
          }
        />
        <Input
          disabled={isDisabled}
          label={t.organization.name}
          type="text"
          value={values.name}
          onChange={(e) => onChange({ ...values, name: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={t.organization.email}
          type="email"
          value={values.email}
          onChange={(e) => onChange({ ...values, email: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={t.organization.phone}
          type="tel"
          value={values.phone}
          onChange={(e) => onChange({ ...values, phone: e.target.value })}
        />
        <AdditionalFields
          additionalFields={additionalFields}
          disabled={isDisabled}
          values={values.additionalFields ?? {}}
          onChange={(additionalFieldValues) =>
            onChange({
              ...values,
              additionalFields: {
                ...additionalFieldValues,
              },
            })
          }
        />
      </div>
      <div className="flex gap-2 justify-end">
        {hasError && (
          <p className="text-red-500 flex items-center gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {t.organization.error}
          </p>
        )}
        <Button disabled={disabled} severity="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={disabled || !isValid} loading={loading} type="submit">
          Save
        </Button>
      </div>
    </form>
  );
}
